import CT from '@marketplace-ai/tags';
import { useSelector } from '@xstate/react';
import classNames from 'classnames';
import React from 'react';
import { createEmptyActor, type ActorRefFrom } from 'xstate';
import { useConfirmationView } from '~/app';
import { EmptyState, LoadingSpinner, TableCard } from '~/components';
import { BasicGrid, type BasicGridRef } from '~/components/agGrid/Grid';
import { PROVIDER_PAGE_KEYS } from '~/routing/routes';
import { useDocumentsRef, useDocumentsService, useLoadedProvider } from '../account/vaultSelectors';
import { CreateDocumentColumns } from './constants';
import { DocumentInfoForm } from './DocumentInfoForm';
import { documentsViewMachine } from './documents-view.machine';
import { documentsService } from './documents.service.machine';
import { ModalDialog } from './ModalDialog';
import type { DocumentWithAiJobs } from './types';
import { REPARSE_CONFIRM_MESSAGE, needsReparseConfirm } from './utils';
const TAGS = CT.documents;
const emptyActor = createEmptyActor();

const DocumentsNoRowsOverlay = () => (
  <EmptyState
    pageKey={PROVIDER_PAGE_KEYS.documents}
    title="No documents yet"
    cyTag={TAGS.table.emptyState}
  />
);

type DocumentsViewState = {
  modalOpen: boolean;
  fileDrag: boolean;
};

type DocumentsServiceState = {
  documentReferences: DocumentWithAiJobs[];
  loading: boolean;
};

const selectDocumentsViewContext = (state: unknown): DocumentsViewState => {
  if (!state || typeof state !== 'object' || !('context' in state)) {
    return { modalOpen: false, fileDrag: false };
  }

  // createEmptyActor()'s snapshot has `context` as a defined KEY but `undefined` VALUE,
  // so the `'context' in state` guard above passes. Fall back to defaults when the
  // value is actually missing to avoid TypeError on `ctx.modalOpen`.
  const ctx = (state as ReturnType<ActorRefFrom<typeof documentsViewMachine>['getSnapshot']>)
    .context;
  return {
    modalOpen: ctx?.modalOpen ?? false,
    fileDrag: ctx?.fileDrag ?? false,
  };
};

const selectDocumentsServiceContext = (state: unknown): DocumentsServiceState => {
  if (!state || typeof state !== 'object' || !('context' in state)) {
    return {
      documentReferences: [],
      loading: false,
    };
  }

  // Same emptyActor caveat — guard against `context: undefined`.
  const ctx = (state as ReturnType<ActorRefFrom<typeof documentsService>['getSnapshot']>).context;
  return {
    documentReferences: ctx?.documentReferences ?? [],
    loading: ctx?.loading ?? false,
  };
};

export const DocumentsView = () => {
  const gridRef = React.useRef<BasicGridRef>(null);

  // Service and provider come from vault context directly. The service is spawned
  // at vault-context creation (vaultState.machine.ts), so it's available the moment
  // the vault is alive — independent of whether the vault has transitioned to
  // $_DOCUMENTS_VIEW.
  const { documentsService, provider } = useDocumentsService();
  const confirmationRef = useConfirmationView();

  // The view actor is the invoked child of $_DOCUMENTS_VIEW. It may be undefined
  // if the vault is in a different state — we keep rendering data from the service
  // regardless, and guard the handlers that need the actor.
  const { documentsView: actor, loading } = useDocumentsRef();

  // Vault ref — used to self-heal the state machine if it isn't in $_DOCUMENTS_VIEW
  // (e.g. provider-load errored and fell back to $_DASHBOARD_VIEW while the URL
  // still reads /vault/documents).
  const { vaultRef } = useLoadedProvider();

  const viewState = useSelector(actor ?? emptyActor, selectDocumentsViewContext);
  const serviceState = useSelector(documentsService ?? emptyActor, selectDocumentsServiceContext);

  // Self-heal: if the vault state machine isn't in $_DOCUMENTS_VIEW, re-dispatch
  // ON_SET_PAGE so the view actor gets invoked. This enables modal/drag features.
  React.useEffect(() => {
    if (!vaultRef) return;
    const snap = vaultRef.getSnapshot();
    const stateValue = String(snap.value);
    if (!stateValue.includes('$_DOCUMENTS_VIEW')) {
      vaultRef.send({ type: 'ON_SET_PAGE', payload: { page: 'documents' } });
    }
  }, [vaultRef]);

  // Drive data loading directly. The service handles caching so repeated
  // dispatches are cheap.
  React.useEffect(() => {
    if (provider?.id && documentsService) {
      documentsService.send({ type: 'GET_DOCUMENTS', providerId: provider.id });
    }
  }, [provider?.id, documentsService]);

  // MCV-605/1190: the view actor is invoked with providerId from the vault
  // context at the moment $_DOCUMENTS_VIEW is entered. When the self-heal
  // effect below forces that transition before the vault's $_LOAD_PROVIDER
  // has resolved, the view actor is born with providerId === undefined and
  // every subsequent upload is silently dropped. Re-dispatch the id to the
  // view actor once both sides exist so the upload path has a valid target.
  React.useEffect(() => {
    if (provider?.id && actor) {
      actor.send({ type: 'ON_SET_PROVIDER_ID', payload: { providerId: provider.id } });
    }
  }, [provider?.id, actor]);

  // Init gate covers only infrastructure readiness (UserContext loaded + vault
  // spawned + documentsService wired). Provider resolution is backend-slow-sensitive
  // and is surfaced via the grid's own loading state below, not this spinner —
  // otherwise a slow `waitForProvider` would wrongly trip the 4s stuck-fallback.
  const isStillInitializing = loading || !documentsService;

  // Escape hatch: if we're still initializing after ~4 seconds something is wedged
  // (UserContext stuck loading, vault never spawned, documentsService spawn errored).
  // Surface a Reload option so the user isn't stranded on an infinite spinner.
  const [showStuckFallback, setShowStuckFallback] = React.useState(false);
  React.useEffect(() => {
    if (!isStillInitializing) {
      setShowStuckFallback(false);
      return;
    }
    const timerId = window.setTimeout(() => {
      console.warn('[DocumentsView] Still loading after 4s', {
        userContextLoading: loading,
        hasDocumentsService: !!documentsService,
        hasViewActor: !!actor,
      });
      setShowStuckFallback(true);
    }, 4000);
    return () => window.clearTimeout(timerId);
  }, [isStillInitializing, loading, documentsService, actor]);

  if (isStillInitializing) {
    if (showStuckFallback) {
      return (
        <div
          data-cy={TAGS.layout}
          className="flex flex-col items-center justify-center gap-3 py-16 text-center"
        >
          <p className="text-grey-100 font-semibold">
            Documents are taking longer than usual to load.
          </p>
          <p className="text-sm text-grey-500 max-w-md">
            This usually clears on reload. If it keeps happening, try signing out and back in.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-2 px-4 py-2 bg-primary-50 text-white rounded-md hover:bg-primary-100 transition-colors cursor-pointer"
          >
            Reload page
          </button>
        </div>
      );
    }
    return (
      <div data-cy={TAGS.layout} className="flex items-center justify-center min-h-[40vh] w-full">
        <LoadingSpinner size={24} gap={4} />
      </div>
    );
  }

  const { modalOpen, fileDrag } = viewState;
  const { documentReferences } = serviceState;
  // Keep the grid in its own loading state while the provider is still resolving,
  // so we don't flash a misleading "No documents" empty state before GET_DOCUMENTS fires.
  const documentsLoading = serviceState.loading || !provider;

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  // All actor-bound handlers explicitly bail when the view actor isn't spawned
  // (the narrow render window after self-heal dispatches ON_SET_PAGE but before
  // the vault machine transitions to $_DOCUMENTS_VIEW). The Add button is
  // disabled during this window via TableCard's disabledBtnTooltip above, so
  // users have a visible affordance; the drag/drop path just no-ops silently.
  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!actor) return;
    actor.send({ type: 'ON_DRAG_ENTER' });
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!actor) return;
    actor.send({ type: 'ON_DRAG_LEAVE' });
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!actor) return;
    actor.send({ type: 'ON_DROP_UPLOAD', payload: { files: e.dataTransfer.files as FileList } });
  };

  // Actions that go through the view actor when present (so the modal flow and
  // context stay consistent), but fall through to the service directly when not.
  // These service events are pass-throughs in the view machine today anyway.
  const handleEdit = (id: number) => actor?.send({ type: 'ON_OPEN_MODAL', payload: { id } });
  const handleDelete = (id: number) => {
    if (actor) actor.send({ type: 'ON_DELETE_FILE', payload: { id } });
    else documentsService?.send({ type: 'DELETE_DOCUMENT', id });
  };
  const handleDownload = (id: number) => {
    if (actor) actor.send({ type: 'ON_DOWNLOAD_FILE', payload: { id } });
    else documentsService?.send({ type: 'DOWNLOAD_DOCUMENT', id });
  };
  const handleParse = (id: number) => {
    const parse = () => {
      if (actor) actor.send({ type: 'ON_TRIGGER_AI_PARSE', payload: { id } });
      else documentsService?.send({ type: 'AI_PARSE_DOCUMENT', id });
    };
    const doc = documentReferences.find((d) => d.id === id);
    if (!needsReparseConfirm(doc)) {
      parse();
      return;
    }
    // [MCV-1761] The app's confirmation dialog, not the browser's native one.
    confirmationRef.send({
      type: 'ON_SHOW_CONFIRMATION',
      payload: {
        title: 'Parse again?',
        message: REPARSE_CONFIRM_MESSAGE,
        confirmLabel: 'Parse',
        tone: 'neutral',
        onConfirm: parse,
      },
    });
  };
  const handleOnClose = () => actor?.send({ type: 'ON_CLOSE_MODAL' });

  return (
    <>
      <TableCard
        mobilePageHeader
        title="Uploaded Documents"
        addLabel="Add Document"
        // Open the metadata modal first so users categorize the document up
        // front. The modal includes an in-form file picker that kicks off the
        // upload asynchronously; metadata fields stay editable throughout.
        onAddClick={() => actor?.send({ type: 'ON_OPEN_ADD_MODAL' })}
        addBtnTag={TAGS.uploadFileButton}
        layoutTag={TAGS.layout}
        disabledBtnTooltip={!actor}
        disabledTitle={!actor ? 'Setting up document uploads…' : undefined}
      >
        <div
          data-cy={TAGS.table.dropUploadTarget}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={classNames(
            'relative',
            fileDrag ? 'outline-2 outline-offset-2 outline-accent1' : ''
          )}
        >
          {fileDrag && (
            <div className="absolute inset-0 bg-accent1 bg-opacity-50 pointer-events-none z-10 flex items-center justify-center">
              <div className="text-accent1-dark text-xl font-semibold">
                Drop files here to upload
              </div>
            </div>
          )}
          <BasicGrid
            ref={gridRef}
            data={documentReferences}
            loading={documentsLoading}
            colDefs={CreateDocumentColumns({
              handleEdit,
              handleDelete,
              handleDownload,
              handleParse,
            })}
            pagination={true}
            cyTag={TAGS.table.grid}
            noRowsOverlayComponent={DocumentsNoRowsOverlay}
            getRowId={(params) => {
              // Include _lastUpdated timestamp to force re-renders when data changes
              // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ag-grid data is any for generic usage
              const doc = params.data as any;
              return `${doc.id}-${doc._lastUpdated || doc.updatedAt}`;
            }}
          />
        </div>
      </TableCard>
      {actor && (
        <ModalDialog title={'Upload File Details'} open={modalOpen} onClose={handleOnClose}>
          <DocumentInfoForm actor={actor} />
        </ModalDialog>
      )}
    </>
  );
};
